import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";

import type { CollectionEntryDto, UpdateCollectionEntriesBody } from "./collectionsApi";

export type CollectionEntryId = number | string;

export interface CollectionEntry {
	id: CollectionEntryId;
	value: string;
	createdAt: string;
	updatedAt: string;
}

export function isTempEntryId(id: CollectionEntryId): boolean {
	return typeof id === "string" && id.startsWith("temp-");
}

export function createDraftCollectionEntry(value = "", timestamp = new Date().toISOString()): CollectionEntry {
	return {
		id: `temp-${crypto.randomUUID()}`,
		value,
		createdAt: timestamp,
		updatedAt: timestamp,
	};
}

export function collectionEntryFromDto(dto: CollectionEntryDto): CollectionEntry {
	return {
		id: dto.id,
		value: dto.value,
		createdAt: dto.createdAt,
		updatedAt: dto.updatedAt,
	};
}

export function buildEntrySaveDiff(
	savedEntries: CollectionEntry[],
	draftEntries: CollectionEntry[],
): UpdateCollectionEntriesBody {
	const draftById = new Map(
		draftEntries.filter((e) => !isTempEntryId(e.id)).map((e) => [String(e.id), e]),
	);
	const removedEntryIds: number[] = savedEntries
		.filter((e) => !isTempEntryId(e.id) && !draftById.has(String(e.id)))
		.map((e) => Number(e.id));

	const addedEntries = draftEntries
		.filter((e) => isTempEntryId(e.id))
		.map((e) => ({ value: e.value }));

	const updatedEntries = draftEntries
		.filter((e) => {
			if (isTempEntryId(e.id)) return false;
			const prior = savedEntries.find((s) => String(s.id) === String(e.id));
			return prior != null && prior.value !== e.value;
		})
		.map((e) => ({ id: Number(e.id), value: e.value }));

	return { removedEntryIds, addedEntries, updatedEntries };
}

export function entryIdsEqual(a: CollectionEntryId, b: CollectionEntryId): boolean {
	return String(a) === String(b);
}

export function normalizeCollectionEntry(entry: CollectionEntry): CollectionEntry {
	const fallback = new Date().toISOString();
	return {
		...entry,
		createdAt: entry.createdAt ?? fallback,
		updatedAt: entry.updatedAt ?? fallback,
	};
}

export function normalizeEntryValueForComparison(value: string, dataType: VariableDataType): string {
	const trimmed = value.trim();
	switch (dataType) {
		case "boolean":
			return trimmed.toLowerCase();
		case "number":
		case "decimal":
			return trimmed === "" ? "" : String(Number(trimmed));
		default:
			return trimmed.toLowerCase();
	}
}

export function entryValueIsEmpty(value: string): boolean {
	return value.trim() === "";
}

export function findDuplicateEntryIndices(
	entries: CollectionEntry[],
	dataType: VariableDataType,
): Set<number> {
	const seen = new Map<string, number>();
	const dupes = new Set<number>();
	entries.forEach((entry, index) => {
		const key = normalizeEntryValueForComparison(entry.value, dataType);
		if (key === "") return;
		const prior = seen.get(key);
		if (prior !== undefined) {
			dupes.add(prior);
			dupes.add(index);
		} else {
			seen.set(key, index);
		}
	});
	return dupes;
}
